/**
 * AuthContext — single source of truth for the current admin session.
 *
 * Cookie-based session (Feb 2026): the browser holds an httpOnly `civi_session`
 * cookie that's auto-attached by axios (`withCredentials: true`). We can't
 * READ it from JS — we discover the session by hitting GET /api/auth/me.
 *
 * States:
 *   user === null    → still checking (mount-time bootstrap in flight)
 *   user === false   → confirmed unauthenticated
 *   user === object  → authenticated; shape is {email, id}
 *
 * Components consume `useAuth()` to read + mutate this state.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { clearCache } from "@/lib/swr";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = authed

  // Bootstrap on mount — concurrency-safe via the `cancelled` flag so a
  // remount mid-flight doesn't leave a stale setState dangling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) setUser(r.data);
      } catch {
        if (!cancelled) setUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(false);
    }
  }, []);

  const login = useCallback(async (email, password, turnstileToken) => {
    const r = await api.post("/auth/login", { email, password, turnstile_token: turnstileToken || null });
    // Cookie is now set by the server. Populate context so the SPA can route.
    setUser({ email: r.data.email, id: null });
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* server-side cookie clear is best-effort */ }
    clearCache("sites"); clearCache("folders");
    setUser(false);
  }, []);

  // Memoize the context value so consumers don't re-render when AuthProvider's
  // parent re-renders. login/logout/refresh are already stable (useCallback).
  const value = useMemo(() => ({ user, login, logout, refresh }), [user, login, logout, refresh]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}
